import type { GetServerSidePropsContext } from 'next'
import { getCookie as getCookieInClient, setCookie as setCookieInClient } from 'cookies-next/client'
import { getCookie as getCookieInServer, setCookie as setCookieInServer } from 'cookies-next/server'
import {
  AxiosError,
  AxiosResponse,
  type AxiosResponseHeaders,
  type RawAxiosResponseHeaders,
} from 'axios'
import isNil from 'lodash/isNil'
import isEmpty from 'lodash/isEmpty'
import isNull from 'lodash/isNull'
import isObject from 'lodash/isObject'
import { JWT_COOKIE } from '../../../constants/cookies'
import sendLog from '../sendLog'
import { getExpiryFromJwt } from '../../utils/jwtUtils'

export const getJwtFromCookieInClient = (name = JWT_COOKIE) => {
  try {
    const cookie = getCookieInClient(name, { secure: true })
    return cookie
  } catch {
    // We do not want to log an error here because
    // this function may be called when there are no jwt or id_token cookies
    return undefined
  }
}

export const getJwtFromCookieInServer = async (
  context: GetServerSidePropsContext,
  name = JWT_COOKIE
) => {
  const options = context ? { ...context, secure: true } : { secure: true }

  try {
    const cookie = await getCookieInServer(name, options)
    return cookie
  } catch {
    sendLog('API: failed to get JWT cookie server side')
    return undefined
  }
}

export const getJwtFromHeader = (headers: RawAxiosResponseHeaders | AxiosResponseHeaders) =>
  (headers.authorization ?? headers.Authorization)?.split(' ')[1]

export const setJwtCookieInClient = (jwt: string) => {
  try {
    const exp = getExpiryFromJwt(jwt)

    setCookieInClient(JWT_COOKIE, jwt, {
      secure: true,
      expires: exp ? new Date(exp * 1000) : undefined,
    })
  } catch {
    sendLog('API: failed to set JWT cookie client side')
  }
}

export const setJwtCookieInServer = async (context: GetServerSidePropsContext, jwt: string) => {
  const options = {
    ...context,
    secure: true,
  }

  try {
    const exp = getExpiryFromJwt(jwt)

    await setCookieInServer(JWT_COOKIE, jwt, {
      ...options,
      expires: exp ? new Date(exp * 1000) : undefined,
    })
  } catch {
    sendLog('API: failed to set JWT cookie server side')
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const axiosTransformResponse = (data: any) => {
  // Handle empty responses (e.g., 204 No Content)
  if (isEmpty(data) || isNil(data)) {
    return null
  }

  // If data is already an object, return it as-is
  if (isObject(data) && !isNull(data)) {
    return data
  }

  try {
    return JSON.parse(data)
  } catch (error) {
    const errMessage = error instanceof Error ? error.message : ''
    const err = new Error(`API: failed to parse JSON response in transformResponse. ${errMessage}`)

    sendLog(err)

    return data
  }
}

export const getAxiosErrorStatusCode = (error: AxiosError) =>
  error.status ?? error?.response?.status ?? 0

export const generateAxiosError = (statusOverride: number, code?: string) =>
  new AxiosError(`Generated Axios Error`, code, undefined, undefined, {
    status: statusOverride,
  } as AxiosResponse)
